import React, { createContext, useState, useCallback } from 'react';

export interface SheetAction {
  content?: React.ReactNode;
  onAction?: (...args: unknown[]) => void;
  [extra: string]: unknown;
}

export interface SheetState {
  title: React.ReactNode;
  content?: React.ReactNode;
  primaryAction: SheetAction | null;
  secondaryAction: SheetAction | null;
  isError: boolean;
  loading: boolean;
  visible: boolean;
  newUploads: unknown[];
  type: string | null;
  defaultTab: number;
  /** show() spreads caller args verbatim; tolerate extras without widening reads. */
  [extra: string]: unknown;
}

export interface SheetContextValue {
  show: (args?: Partial<SheetState>) => void;
  hide: () => void;
  loading: (val: boolean) => void;
  sheet: SheetState;
  clearUploads: () => void;
}

const initialSheet: SheetState = {
  title: '',
  content: '',
  primaryAction: null,
  secondaryAction: {
    content: 'Cancel',
  },
  isError: false,
  loading: false,
  visible: false,
  newUploads: [],
  type: null,
  defaultTab: 0,
};

// Default is a cast, not null: every consumer lives under the provider, and the
// ~30 existing `useContext` destructures must keep compiling unchanged (B1).
export const SheetContext = createContext<SheetContextValue>({} as SheetContextValue);

export const SheetProvider = ({ children }: { children: React.ReactNode }) => {
  const [sheet, setSheet] = useState<SheetState>(initialSheet);

  const clearUploads = useCallback(() => {
    setSheet((prevState) => ({
      ...prevState,
      newUploads: [],
    }));
  }, []);

  const show = useCallback(
    (args?: Partial<SheetState>) => {
      setSheet({ ...initialSheet, visible: true, ...args });
    },
    [sheet],
  );

  const loading = useCallback((val: boolean) => {
    setSheet((prevState) => ({
      ...prevState,
      loading: val,
    }));
  }, []);

  const hide = useCallback(() => {
    setSheet({ ...sheet, visible: false });
  }, [sheet]);

  return (
    <SheetContext.Provider
      value={{
        hide,
        show,
        loading,
        sheet,
        clearUploads,
      }}
    >
      {children}
    </SheetContext.Provider>
  );
};
